import { useState, useEffect, useMemo } from "react";
import styles from "./FlightStatus.module.css";
import ViewStatusModal from "../../../../components/Products/Flights/FlightStatus/ViewStatusModal";
import { Search, Info, Clock, AlertTriangle, XCircle, CheckCircle, Calendar, RefreshCw } from "lucide-react";
import { useGetFlightStatuses } from "../../../../api/products/useFlightStatusesApi";

const FlightStatus = () => {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: statuses = [], loading, error, refetch } = useGetFlightStatuses({
    page: 1,
    limit: 100,
    search: debouncedSearch,
  });

  const grouped = useMemo(() => {
    const preDeparture = statuses.filter(s => s.category === "Pre-Departure");
    const changes = statuses.filter(s => s.category === "Schedule Change");
    const disruptions = statuses.filter(s => s.category === "Disruption");
    return { preDeparture, changes, disruptions };
  }, [statuses]);

  const allStatuses = statuses;
  const hasResults = grouped.preDeparture.length > 0 || grouped.changes.length > 0 || grouped.disruptions.length > 0;

  const handleViewDetails = (status) => {
    setSelectedStatus(status);
    setShowViewModal(true);
  };

  const handleRefresh = () => {
    refetch();
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1>Flight Status</h1>
          <p className={styles.subtitle}>
            Authoritative flight statuses used across API flights, manual bookings, schedule updates, and customer communications
          </p>
        </div>
        <button className={styles.refreshBtn} onClick={handleRefresh}>
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      {/* Info Banner */}
      <div className={styles.infoBanner}>
        <div className={styles.infoIcon}>
          <Info size={20} />
        </div>
        <div className={styles.infoText}>
          <strong>Read-Only Configuration:</strong> Flight statuses are system-defined and cannot be modified. These statuses are used by API integrations, manual flight operations, schedule updater, booking documents, and customer communication systems.
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className={styles.infoBanner} style={{ background: "#fef2f2", borderColor: "#fecaca" }}>
          <div className={styles.infoIcon}>⚠️</div>
          <div className={styles.infoText}>
            {error}
            <button className={styles.retryBtn} onClick={() => refetch()}>
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className={styles.searchBox}>
        <Search size={18} className={styles.searchIcon} />
        <input
          type="text"
          placeholder="Search by status name, code, or description..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </div>

      {/* Stats */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#dbeafe' }}>
            <CheckCircle size={24} color="#2563eb" />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{grouped.preDeparture.length}</div>
            <div className={styles.statLabel}>Pre-Departure</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#fef3c7' }}>
            <Clock size={24} color="#d97706" />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{grouped.changes.length}</div>
            <div className={styles.statLabel}>Schedule Changes</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#fee2e2' }}>
            <XCircle size={24} color="#dc2626" />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{grouped.disruptions.length}</div>
            <div className={styles.statLabel}>Disruptions</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#e0e7ff' }}>
            <Calendar size={24} color="#6366f1" />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{allStatuses.length}</div>
            <div className={styles.statLabel}>Total Statuses</div>
          </div>
        </div>
      </div>

      {loading && (
        <div className={styles.noResults}>
          <p>Loading flight statuses…</p>
        </div>
      )}

      {!loading && !hasResults && searchInput && (
        <div className={styles.noResults}>
          <div className={styles.noResultsIcon}>🔍</div>
          <p>No statuses found matching "{searchInput}"</p>
          <span>Try a different search term</span>
        </div>
      )}

      {/* Pre-Departure Section */}
      {!loading && grouped.preDeparture.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              <CheckCircle size={20} color="#2563eb" />
              <h2>Pre-Departure Statuses</h2>
            </div>
            <span className={styles.badge}>{grouped.preDeparture.length} statuses</span>
          </div>
          <div className={styles.statusGrid}>
            {grouped.preDeparture.map(status => (
              <div
                key={status.id}
                className={styles.statusCard}
                onClick={() => handleViewDetails(status)}
              >
                <div className={styles.statusHeader}>
                  <div className={styles.statusIcon} style={{ background: `${status.color}15` }}>
                    <span style={{ fontSize: '32px' }}>{status.icon}</span>
                  </div>
                  <div className={styles.statusBadge} style={{ background: `${status.color}15`, color: status.color }}>
                    {status.code}
                  </div>
                </div>
                <h3 className={styles.statusName} style={{ color: status.color }}>{status.status}</h3>
                <p className={styles.statusDesc}>{status.description}</p>
                <div className={styles.statusFooter}>
                  <span className={styles.usageLabel}>Used by {status.usedBy.length} systems</span>
                  <button className={styles.viewBtn}>View Details →</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Schedule Changes Section */}
      {!loading && grouped.changes.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              <Clock size={20} color="#d97706" />
              <h2>Schedule Changes</h2>
            </div>
            <span className={styles.badge}>{grouped.changes.length} statuses</span>
          </div>
          <div className={styles.statusGrid}>
            {grouped.changes.map(status => (
              <div
                key={status.id}
                className={styles.statusCard}
                onClick={() => handleViewDetails(status)}
              >
                <div className={styles.statusHeader}>
                  <div className={styles.statusIcon} style={{ background: `${status.color}15` }}>
                    <span style={{ fontSize: '32px' }}>{status.icon}</span>
                  </div>
                  <div className={styles.statusBadge} style={{ background: `${status.color}15`, color: status.color }}>
                    {status.code}
                  </div>
                </div>
                <h3 className={styles.statusName} style={{ color: status.color }}>{status.status}</h3>
                <p className={styles.statusDesc}>{status.description}</p>
                <div className={styles.statusFooter}>
                  <span className={styles.usageLabel}>Used by {status.usedBy.length} systems</span>
                  <button className={styles.viewBtn}>View Details →</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Disruptions Section */}
      {!loading && grouped.disruptions.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              <AlertTriangle size={20} color="#dc2626" />
              <h2>Disruptions</h2>
            </div>
            <span className={styles.badge}>{grouped.disruptions.length} statuses</span>
          </div>
          <div className={styles.statusGrid}>
            {grouped.disruptions.map(status => (
              <div
                key={status.id}
                className={styles.statusCard}
                onClick={() => handleViewDetails(status)}
              >
                <div className={styles.statusHeader}>
                  <div className={styles.statusIcon} style={{ background: `${status.color}15` }}>
                    <span style={{ fontSize: '32px' }}>{status.icon}</span>
                  </div>
                  <div className={styles.statusBadge} style={{ background: `${status.color}15`, color: status.color }}>
                    {status.code}
                  </div>
                </div>
                <h3 className={styles.statusName} style={{ color: status.color }}>{status.status}</h3>
                <p className={styles.statusDesc}>{status.description}</p>
                <div className={styles.statusFooter}>
                  <span className={styles.usageLabel}>Used by {status.usedBy.length} systems</span>
                  <button className={styles.viewBtn}>View Details →</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* View Status Details Modal */}
      <ViewStatusModal
        open={showViewModal}
        onClose={() => setShowViewModal(false)}
        status={selectedStatus}
      />
    </div>
  );
};

export default FlightStatus;